/**
 * Reconstruct URLs from terminal output that the program (e.g. `claude login`)
 * hard-wrapped across multiple lines to fit the PTY width.
 *
 * The naive `text.match(/https?:\/\/[^\s\r\n]+/g)` stops at the first newline, so a
 * long OAuth URL printed as
 *
 *     https://claude.com/cai/oauth/authorize?code=
 *     true&client_id=9d1c250a-...
 *     ...&state=z65tr5G_s...
 *
 * is captured as just `https://claude.com/cai/oauth/authorize?code=` — useless for the
 * tap-to-open SshLinkBar chip on mobile (issue: Protofac BYO-subscription `claude login`).
 *
 * A hard-wrap inserts a newline with NO surrounding whitespace, and the next line
 * continues with URL-safe characters. A *genuine* URL end is marked by real whitespace
 * (space/tab), a quote/angle bracket, or a blank line (the program prints the URL block,
 * then a blank line, then the next prompt). So: walk from each `https?://`, treating a
 * single newline as a wrap artifact (skip it, keep going) UNLESS it is followed by
 * whitespace or another newline (blank line) — those terminate the URL.
 */
export function reconstructWrappedUrls(text: string): string[] {
  const out: string[] = [];
  const starts = /https?:\/\//g;
  let m: RegExpExecArray | null;
  while ((m = starts.exec(text)) !== null) {
    let i = m.index;
    let url = '';
    while (i < text.length) {
      const c = text[i];
      if (c === '\n' || c === '\r') {
        // Skip a single CR/LF/CRLF, then decide: continuation or end?
        let j = i;
        if (text[j] === '\r') j++;
        if (text[j] === '\n') j++;
        const next = text[j];
        // End of buffer, blank line, or whitespace-led line → URL ended.
        if (j >= text.length || next === '\n' || next === '\r' || next === ' ' || next === '\t') {
          break;
        }
        // Otherwise the newline is a wrap artifact — drop it and keep consuming.
        i = j;
        continue;
      }
      // Real URL terminators.
      if (c === ' ' || c === '\t' || c === '"' || c === "'" || c === '<' || c === '>') break;
      url += c;
      i += 1;
    }
    // Trim trailing punctuation that is almost never part of the URL.
    url = url.replace(/[.,;:)\]}]+$/, '');
    if (url.length > 'https://'.length) out.push(url);
  }
  return [...new Set(out)];
}

/**
 * Resolve a (possibly hard-wrap-truncated) URL that xterm's WebLinksAddon
 * captured from a single terminal line back to its full form, using the
 * reconstructed-URL list from the same output. WebLinksAddon stops at the first
 * PTY hard-wrap, so the clicked uri is often a prefix of the real URL
 * (e.g. ".../authorize?code=" → ".../authorize?code=true&client_id=…").
 * Returns the exact match if present, else the LONGEST candidate that starts
 * with the clicked uri, else the clicked uri unchanged.
 */
export function resolveFullUrl(clicked: string, candidates: string[]): string {
  if (candidates.includes(clicked)) return clicked;
  let best = clicked;
  for (const c of candidates) {
    if (c.startsWith(clicked) && c.length > best.length) best = c;
  }
  return best;
}
